import {
  faExclamationTriangle,
  faPlus,
  faTrophy,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useState } from "react";
import { Alert, Button, Col, Container, Modal, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useToasts } from "../../contexts/toasts";
import { Tournament } from "../../types/Tournaments";
import { Tournaments } from "../../api/Tournaments";
import { TailSpin } from "react-loader-spinner";
import TournamentItem from "../../Components/list-items/TournamentItem";

export default function TournamentsList() {
  const navigate = useNavigate();
  const { showToast } = useToasts();
  const [tournaments, setTournaments] = useState<Tournament[]>();
  const [error, setError] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [tournamentToDelete, setTournamentToDelete] = useState<Tournament>();
  const [deleteLoading, setDeleteLoading] = useState(false);

  const initTournaments = useCallback(() => {
    setError(false);
    setTournaments(undefined);
    const params = new URLSearchParams();
    params.append("all", "true");
    Tournaments.getTournaments(params).then(
      ({ data }) => setTournaments(data.list),
      () => {
        setTournaments([]);
        setError(true);
        showToast("tournaments-retrieve-error", "Errore nella lista dei Tornei, per favore riprova.", "", "danger");
      }
    );
  }, [showToast]);

  const reset = useCallback(() => initTournaments(), [initTournaments]);

  useEffect(() => { initTournaments(); }, [initTournaments]);

  const navigateToCreation = () => navigate("/tornei/nuovo");
  const editTournament = (tournament: Tournament) => (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation();
    navigate(`/tornei/${tournament.id}/modifica`);
  };
  const onTournamentDetail = (tournament: Tournament) => (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation();
    navigate(`/tornei/${tournament.id}`);
  };
  const openDeleteModal = (tournament: Tournament) => (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation();
    setShowDeleteModal(true);
    setTournamentToDelete(tournament);
  };
  const handleClose = useCallback(() => {
    setShowDeleteModal(false);
    setTournamentToDelete(undefined);
  }, []);

  const deleteTournament = useCallback(() => {
    setDeleteLoading(true);
    Tournaments.deleteTournament(tournamentToDelete!.id!).then(
      () => {
        showToast("delete-tournament-success", "Torneo eliminato con successo", "", "success");
        setDeleteLoading(false);
        reset();
        handleClose();
      },
      () => {
        showToast("delete-tournament-error", "Errore nella cancellazione del Torneo", "", "danger");
        setDeleteLoading(false);
      }
    );
  }, [showToast, reset, handleClose, tournamentToDelete]);

  const renderSkeletons = () => (
    <Row>
      {[1, 2, 3, 4, 5, 6].map((_, i) => (
        <Col xs={12} sm={6} md={4} lg={3} className="p-2" key={i}>
          <div
            style={{
              borderRadius: 12,
              overflow: "hidden",
              border: "1px solid #e8e8e8",
              background: "#fff",
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            }}
          >
            <div className="skeleton-bar" style={{ height: 4, background: "#e0e0e0" }} />
            <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ height: 16, borderRadius: 8, background: "#efefef", width: "70%" }} />
              <div style={{ height: 12, borderRadius: 8, background: "#f5f5f5", width: "50%" }} />
              <div style={{ height: 12, borderRadius: 8, background: "#f5f5f5", width: "60%" }} />
              <div style={{ display: "flex", gap: 6 }}>
                <div style={{ height: 18, width: 52, borderRadius: 20, background: "#e8f0fe" }} />
                <div style={{ height: 18, width: 44, borderRadius: 20, background: "#f0f0f0" }} />
              </div>
            </div>
            <div style={{ height: 42, borderTop: "1px solid #f0f0f0", background: "#fafafa" }} />
          </div>
        </Col>
      ))}
    </Row>
  );

  const renderContent = () => {
    if (!tournaments) return renderSkeletons();

    if (error)
      return (
        <Alert variant="danger" className="py-3">
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
          C'è stato un errore, per favore riprova più tardi
        </Alert>
      );

    if (tournaments.length === 0)
      return (
        <div
          style={{
            textAlign: "center",
            padding: "60px 20px",
            color: "#aaa",
          }}
        >
          <FontAwesomeIcon icon={faTrophy} size="3x" style={{ marginBottom: 16, opacity: 0.3 }} />
          <p style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>Nessun torneo trovato</p>
          <p style={{ fontSize: 13 }}>Crea il primo torneo con il pulsante in alto a destra</p>
        </div>
      );

    return (
      <Row>
        {tournaments.map((tournament, index) => (
          <Col xs={12} sm={6} md={4} lg={3} className="p-2" key={index}>
            <TournamentItem
              onEdit={editTournament(tournament)}
              onView={onTournamentDetail(tournament)}
              onDelete={openDeleteModal(tournament)}
              tournament={tournament}
            />
          </Col>
        ))}
      </Row>
    );
  };

  return (
    <Container fluid="md" className="my-4">
      {/* Page header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
          paddingBottom: 16,
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              background: "#f25d0b",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FontAwesomeIcon icon={faTrophy} color="#fff" size="sm" />
          </div>
          <div>
            <h5 style={{ margin: 0, fontWeight: 700 }}>I Tuoi Tornei</h5>
            {tournaments && (
              <small className="text-muted">{tournaments.length} torneo/i</small>
            )}
          </div>
        </div>
        <button onClick={navigateToCreation} className="button-primary">
          <FontAwesomeIcon icon={faPlus} />
          <span className="d-none d-sm-inline ms-2">Aggiungi Torneo</span>
        </button>
      </div>

      {renderContent()}

      {/* Delete modal */}
      <Modal show={showDeleteModal} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 16 }}>Elimina Torneo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            Sei sicuro di voler eliminare{" "}
            <strong>{tournamentToDelete?.name || "questo torneo"}</strong>?
          </p>
          <Alert variant="warning" className="py-2 mb-0">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            <small>L'azione è irreversibile.</small>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button disabled={deleteLoading} variant="light" size="sm" className="border" onClick={handleClose}>
            {deleteLoading ? <TailSpin color="#000" width={16} height={16} /> : "Annulla"}
          </Button>
          <Button disabled={deleteLoading} variant="danger" size="sm" onClick={deleteTournament}>
            {deleteLoading ? (
              <TailSpin color="#FFF" width={16} height={16} />
            ) : (
              <>
                <FontAwesomeIcon icon={faTrash} className="me-2" />
                Elimina
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
